import { ChevronRightIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IRoadmapMilestone } from '@/MODELS/roadmapMilestone.model'

import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'
import Table from '@/components/Table'

function formatDate(date?: Date | string) {
    if (!date) return 'Unknown'
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AdminRoadmapMilestonesTable() {
    const navigate = useNavigate()
    const [milestones] = useResource<IRoadmapMilestone[]>(ROUTES.admin.roadmapMilestone)
    const [search, setSearch] = useState('')

    const { activeMilestones, inactiveMilestones } = useMemo(() => {
        const active: IRoadmapMilestone[] = []
        const inactive: IRoadmapMilestone[] = []
        for (const m of milestones ?? []) {
            if (m.isActive) active.push(m)
            else inactive.push(m)
        }
        return { activeMilestones: active, inactiveMilestones: inactive }
    }, [milestones])

    const filteredActive = activeMilestones.filter((m) => {
        if (!search) return true
        const q = search.toLowerCase()
        return (
            m.key?.toLowerCase().includes(q) ||
            m.title?.toLowerCase().includes(q) ||
            m.focus?.toLowerCase().includes(q) ||
            m.description?.toLowerCase().includes(q)
        )
    })

    return (
        <div className='flex flex-col w-full max-w-7xl gap-5 py-10 mx-auto'>
            <AdminPageHeader
                heading='Roadmap Milestones'
                subheading={
                    <>
                        <p>Manage milestone definitions used in AI-generated roadmaps</p>
                        <p className='text-xs text-gray-400 font-normal mt-1'>Note: Milestones cannot be deleted because existing user roadmaps reference them. Set a milestone to Inactive to exclude it from future roadmap generation.</p>
                    </>
                }
                className='py-5'
                action={<Button onClick={() => navigate('/admin/roadmap-milestone/new')}>Add Milestone</Button>}
            />

            <div className='bg-white rounded-xl shadow-sm border border-neutral-200 p-4'>
                <div className='relative max-w-md'>
                    <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400' />
                    <input
                        type='text'
                        placeholder='Search milestones...'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className='w-full rounded-lg border border-neutral-300 pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
                    />
                </div>
            </div>

            <Table
                data={filteredActive}
                hideSearch
                numberOfItemsPerPage={100}
                columns={[
                    {
                        header: 'Order',
                        cell: 'order',
                        sort: (a, b) => (a.order ?? 0) - (b.order ?? 0),
                    },
                    {
                        header: 'Key',
                        cell: (m) => <div className='max-w-[180px] truncate font-mono text-sm'>{m.key}</div>,
                    },
                    {
                        header: 'Title',
                        cell: (m) => <div className='max-w-[220px] truncate'>{m.title}</div>,
                    },
                    {
                        header: 'Focus',
                        cell: (m) => <div className='max-w-[250px] truncate text-gray-500 text-sm'>{m.focus}</div>,
                    },
                    {
                        header: '',
                        cell: (m) => (
                            <Link
                                to={`/admin/roadmap-milestone/${m._id}`}
                                className='transition-opacity hover:opacity-70'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <PencilIcon className='w-6 h-6 shrink-0' />
                            </Link>
                        ),
                    },
                ]}
            />

            {inactiveMilestones.length > 0 && (
                <details className='group'>
                    <summary className='cursor-pointer text-sm font-medium text-gray-500 flex items-center gap-2 py-2 hover:text-gray-700'>
                        <ChevronRightIcon className='w-4 h-4 transition-transform group-open:rotate-90' />
                        Inactive Milestones ({inactiveMilestones.length})
                    </summary>
                    <div className='mt-2 space-y-2'>
                        {inactiveMilestones.map((m) => (
                            <div
                                key={m._id}
                                className='flex items-center justify-between p-3 rounded-lg border border-neutral-200 bg-neutral-50 text-gray-500'
                            >
                                <div className='flex-1 min-w-0'>
                                    <div className='flex items-center gap-3'>
                                        <span className='text-sm font-medium text-gray-700'>{m.title}</span>
                                        <span className='text-xs font-mono text-gray-400'>{m.key}</span>
                                    </div>
                                    <div className='flex items-center gap-4 mt-1 text-xs text-gray-400'>
                                        <span>Created {formatDate(m.createdAt)}</span>
                                        <span>Deactivated {formatDate(m.deactivatedAt)}</span>
                                    </div>
                                </div>
                                <Link
                                    to={`/admin/roadmap-milestone/${m._id}`}
                                    className='transition-opacity hover:opacity-70 shrink-0 ml-3'
                                >
                                    <PencilIcon className='w-5 h-5 text-gray-400' />
                                </Link>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    )
}
